import { VoiceCommand } from '../types/voiceOrderingTypes';

// Trio keywords detection
const TRIO_KEYWORDS = [
  'trio',
  'option trio',
  'en trio',
  'avec trio',
  'formule trio',
  'menu trio'
];

// Enhanced command detection including trio validation
export const detectCommand = (text: string): VoiceCommand => {
  const lowerCase = text.toLowerCase().trim();

  // Start order variations in French
  if (lowerCase.includes('commencer la commande') ||
      lowerCase.includes('commencer commande') ||
      lowerCase.includes('commencer') ||
      lowerCase.includes('nouvelle commande') ||
      lowerCase.includes('passer commande') ||
      lowerCase.includes('débuter commande')) {
    return 'START_ORDER';
  }

  // Thank you / completion in French
  if (lowerCase.includes('merci')) {
    return 'THANK_YOU';
  }

  // Confirm order (FR/EN)
  if (lowerCase.includes('confirmer la commande') || 
      lowerCase.includes('confirmer commande') ||
      lowerCase.includes('c\'est bon') ||
      lowerCase.includes('ok') ||
      lowerCase.includes('ça marche') ||
      lowerCase.includes('validé') ||
      lowerCase.includes('c\'est correct') ||
      lowerCase.includes('nickel') ||
      lowerCase.includes('vas-y') ||
      lowerCase.includes('parfait') ||
      lowerCase.includes('on valide') ||
      lowerCase.includes('that\'s it') ||
      lowerCase.includes('looks good') ||
      lowerCase.includes('sounds good') ||
      lowerCase.includes('okay') ||
      lowerCase.includes('alright') ||
      lowerCase.includes('perfect') ||
      lowerCase.includes('done')) {
    return 'CONFIRM_ORDER';
  }

  // Trio validation command
  if (lowerCase.includes('valider ma demande trio') ||
      lowerCase.includes('valider me demande trio') ||
      lowerCase.includes('valider demande trio') ||
      lowerCase.includes('valider trio') ||
      lowerCase.includes('confirmer trio')) {
    return 'VALIDATE_TRIO';
  }

  // Trio navigation: next group
  if (lowerCase.includes('suivant') ||
      lowerCase.includes('suivante') ||
      lowerCase.includes('prochain') ||
      lowerCase.includes('prochaine') ||
      lowerCase.includes('étape suivante') ||
      lowerCase.includes('groupe suivant')) {
    return 'NEXT_GROUP';
  }

  // Trio navigation: previous group
  if (lowerCase.includes('précédent') ||
      lowerCase.includes('précédente') ||
      lowerCase.includes('étape précédente') ||
      lowerCase.includes('groupe précédent') ||
      lowerCase.includes('retour') ||
      lowerCase.includes('revenir en arrière')) {
    return 'PREV_GROUP';
  }

  // Trio cancel
  if (lowerCase.includes('annuler trio') ||
      lowerCase.includes('annuler la configuration trio') ||
      lowerCase.includes('quitter trio') ||
      lowerCase.includes('stopper trio') ||
      lowerCase.includes('arrêter trio')) {
    return 'CANCEL_TRIO';
  }

  // Trio repeat options
  if (lowerCase.includes('répéter') ||
      lowerCase.includes('répète') ||
      lowerCase.includes('répéter options') ||
      lowerCase.includes('répéter les options') ||
      lowerCase.includes('répéter le choix') ||
      lowerCase.includes('réexpliquer')) {
    return 'REPEAT_OPTIONS';
  }

  // Remove item intents
  // Only trigger native REMOVE_ITEM if it doesn't contain a number (let AI handle relative removals with quantities)
  const hasNumber = /\d/.test(lowerCase) || 
                   lowerCase.includes(' un ') || lowerCase.includes(' une ') ||
                   lowerCase.includes(' deux ') || lowerCase.includes(' trois ') ||
                   lowerCase.includes(' quatre ') || lowerCase.includes(' cinq ');

  if (
    !hasNumber && (
      lowerCase.includes('supprimer ') ||
      lowerCase.includes('enlever ') ||
      lowerCase.includes('retirer ') ||
      lowerCase.includes('enlève-moi') ||
      lowerCase.includes('retire-moi') ||
      lowerCase.includes('sans ça') ||
      lowerCase.includes('pas de') ||
      lowerCase.includes('j\'en veux pas') ||
      lowerCase.includes('enlève juste') ||
      lowerCase.includes('garde pas') ||
      lowerCase.includes('supprime-moi') ||
      lowerCase.includes('l\'enlever') ||
      lowerCase.includes('sans mettre') ||
      lowerCase.includes('no more') ||
      lowerCase.includes('take out') ||
      lowerCase.includes('leave out') ||
      lowerCase.includes('remove that') ||
      lowerCase.includes('without') ||
      lowerCase.includes('hold the') ||
      lowerCase.includes('skip') ||
      lowerCase.startsWith('supprimer') ||
      lowerCase.startsWith('enlever') ||
      lowerCase.startsWith('retirer')
    )
  ) {
    return 'REMOVE_ITEM';
  }

  // Reset commands (FR/EN)
  if (lowerCase.includes('réinitialiser') || 
      lowerCase.includes('nettoyer') ||
      lowerCase.includes('redémarrer') || 
      lowerCase.includes('oublier') ||
      lowerCase.includes('recommencer') ||
      lowerCase.includes('abandonner') ||
      lowerCase.includes('abandonne') ||
      lowerCase.includes('annuler la commande') ||
      lowerCase.includes('annuler commande') ||
      lowerCase.includes('annuler tout') ||
      lowerCase.includes('on recommence') ||
      lowerCase.includes('annule tout ça') ||
      lowerCase.includes('vide la commande') ||
      lowerCase.includes('efface tout') ||
      lowerCase.includes('reset') ||
      lowerCase.includes('j\'annule') ||
      lowerCase.includes('repartir de zéro') ||
      lowerCase.includes('start over') ||
      lowerCase.includes('clear order') ||
      lowerCase.includes('cancel everything') ||
      lowerCase.includes('never mind')) {
    return 'RESET';
  }

  // Activity / "Still here" commands
  if (lowerCase === 'oui' || 
      lowerCase === 'ouais' ||
      lowerCase.includes('je suis la') ||
      lowerCase.includes('je suis là') ||
      lowerCase.includes('toujours là') ||
      lowerCase.includes('présent') ||
      lowerCase.includes('encore là')) {
    return 'STAY_ALIVE';
  }

  return 'NONE';
};

// Extract target product name for removal commands like
// "supprimer big tasty", "retirer trio big tasty", "enlever le coca"
export const extractRemovalTarget = (text: string): string | null => {
  const lower = text.toLowerCase().trim();
  const keywords = [
    'supprimer', 'enlever', 'retirer', 'enlève-moi', 'retire-moi', 'sans ça', 'pas de', 
    'j\'en veux pas', 'enlève juste', 'garde pas', 'supprime-moi', 'l\'enlever', 'sans mettre',
    'no more', 'take out', 'leave out', 'remove that', 'without', 'hold the', 'skip'
  ];

  const firstMatch = keywords
    .map((k) => ({ k, pos: lower.indexOf(k) }))
    .filter(({ pos }) => pos !== -1)
    .sort((a, b) => a.pos - b.pos)[0];

  if (!firstMatch) return null;

  const { k: found, pos: idx } = firstMatch;
  let tail = lower.slice(idx + found.length).trim();
  // Remove leading articles and optional words
  tail = tail.replace(/^(le|la|les|un|une|du|de|des)\s+/i, '');
  tail = tail.replace(/^(trio)\s+/i, '');
  // Trim punctuation
  tail = tail.replace(/[.,;:!?]$/g, '').trim();
  return tail || null;
};

// Check if transcript contains trio keywords
export const containsTrioKeyword = (text: string): boolean => {
  const lowerCase = text.toLowerCase();
  return TRIO_KEYWORDS.some(keyword => lowerCase.includes(keyword));
};

// Extract trio items from transcript
export const extractTrioItems = (text: string): string[] => {
  const lowerCase = text.toLowerCase();
  const trioItems: string[] = [];
  
  // Split by common separators
  const segments = lowerCase.split(/[,;]|\s+et\s+|\s+and\s+/);
  
  segments.forEach(segment => {
    const trimmed = segment.trim();
    
    // Check if this segment contains trio keywords
    if (TRIO_KEYWORDS.some(keyword => trimmed.includes(keyword))) {
      // Extract the product name (words before trio keyword)
      const matchingKeyword = TRIO_KEYWORDS.find(keyword => trimmed.includes(keyword));
      if (matchingKeyword) {
        const beforeKeyword = trimmed.split(matchingKeyword)[0].trim();
        // Remove quantity numbers and common words
        const productName = beforeKeyword
          .replace(/^\d+\s*/, '') // Remove leading numbers
          .replace(/^(un|une|le|la|les|du|de|des)\s+/i, '') // Remove articles
          .trim();
        
        if (productName) {
          trioItems.push(productName);
        }
      }
    }
  });
  
  return trioItems;
};

// Split transcript at trio items
export const splitTranscriptAtTrio = (text: string): {
  beforeTrio: string;
  trioItem: string;
  afterTrio: string;
} => {
  const lowerCase = text.toLowerCase();
  
  // Find the first trio keyword position
  let trioKeywordPos = -1;
  let foundKeyword = '';
  
  TRIO_KEYWORDS.forEach(keyword => {
    const pos = lowerCase.indexOf(keyword);
    if (pos !== -1 && (trioKeywordPos === -1 || pos < trioKeywordPos)) {
      trioKeywordPos = pos;
      foundKeyword = keyword;
    }
  });
  
  if (trioKeywordPos === -1) {
    return {
      beforeTrio: text,
      trioItem: '',
      afterTrio: ''
    };
  }
  
  // Find the start of the trio item (look backwards for product name)
  const beforeKeyword = text.substring(0, trioKeywordPos);
  const afterKeyword = text.substring(trioKeywordPos + foundKeyword.length);
  
  // Split by common separators to find item boundaries
  const beforeSegments = beforeKeyword.split(/[,;]|\s+et\s+|\s+and\s+/);
  const afterSegments = afterKeyword.split(/[,;]|\s+et\s+|\s+and\s+/);
  
  // The trio item is the last segment before + keyword + first part after (if any)
  const trioItemStart = beforeSegments[beforeSegments.length - 1] || '';
  const trioItemEnd = afterSegments[0] ? afterSegments[0].split(/\s+/)[0] : '';
  
  const beforeTrio = beforeSegments.slice(0, -1).join(', ').trim();
  const trioItem = `${trioItemStart} ${foundKeyword} ${trioItemEnd}`.trim();
  const afterTrio = afterSegments.slice(1).join(', ').trim();
  
  return {
    beforeTrio,
    trioItem,
    afterTrio
  };
};

// Utility to check if string is numeric (existing function)
export const isNumeric = (str: any): boolean => {
  if (str === "") return false;
  return /\d/.test(str);
};

// Generate context-aware instructions
export const generateInstructions = (
  phase: string,
  isLoading: boolean,
  loadingDots: number,
  trioSelectionState?: any,
  soloItemsCount?: number,
  trioItemsCount?: number
): string => {
  if (isLoading) {
    const dots = '.'.repeat(loadingDots + 1);
    return `Traitement en cours${dots}`;
  }

  switch (phase) {
    case 'idle':
      return 'Dites "Commencer la commande" pour débuter';
    
    case 'ordering':
      return 'Parlez pour commander. Dites \'merci\' pour terminer, \'oublier\' pour réinitialiser, \'option trio\' pour un menu trio, ou \'supprimer + nom produit\' pour retirer un article.';
    
    case 'trio_selection':
      if (trioSelectionState) {
        const currentGroup = trioSelectionState.trioDetails?.option_groups?.[trioSelectionState.currentGroupIndex];
        if (currentGroup) {
          const options = currentGroup.items.map((item: any) => item.name).join(', ');
          return `Quel ${currentGroup.name.toLowerCase()} : ${options} ?`;
        }
      }
      return 'Sélectionnez vos options trio.';
    
    case 'processing':
      return 'Traitement en cours...';
    case 'confirming':
      return 'Traitement en cours...';
    
    default:
      if (soloItemsCount || trioItemsCount) {
        return 'En attente d\'une commande valide...';
      }
      return 'Dites "Commencer la commande" pour débuter';
  }
};
